import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { ABOUT_TEXT, SERVICES, STATS, ROSTER } from '../data/mock';

const RevealText = ({ text }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const winH = window.innerHeight;
      // when section top reaches 70% of viewport, start; when bottom reaches 30%, finish
      const start = winH * 0.75;
      const end = -rect.height * 0.2;
      const p = (start - rect.top) / (start - end);
      setProgress(Math.max(0, Math.min(1, p)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const words = text.split(' ');
  return (
    <p
      ref={ref}
      className="text-[clamp(28px,4.4vw,72px)] leading-[1.1] font-bold italic tracking-[-0.01em] text-center max-w-[1200px] mx-auto"
    >
      {words.map((w, i) => {
        const wordP = Math.min(1, Math.max(0, (progress - i / words.length) * words.length * 0.9 + 0.1));
        const opacity = 0.08 + 0.92 * wordP;
        const color = wordP > 0.6 ? 'rgba(255,255,255,' + opacity + ')' : 'rgba(255,255,255,' + (0.08 + 0.35 * wordP) + ')';
        return (
          <span key={i} style={{ color, transition: 'color 0.2s ease' }}>
            {w}{' '}
          </span>
        );
      })}
    </p>
  );
};

const About = () => {
  return (
    <section id="about" className="relative pt-32 pb-32">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-16">
          <span className="text-[11px] tracking-[0.35em] text-trillex-orange">— ABOUT</span>
        </div>

        {/* Reveal paragraph */}
        <RevealText text={ABOUT_TEXT} />

        {/* Stats */}
        <div className="mt-28 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm">
          {STATS.map((s) => (
            <div key={s.id} className="bg-white/[0.02] px-8 py-10 flex flex-col items-start gap-2">
              <div className="text-[clamp(32px,3.2vw,56px)] font-black text-white tracking-tight">{s.value}</div>
              <div className="text-[11px] tracking-[0.3em] text-white/50 uppercase">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Services */}
        <div className="mt-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-[11px] tracking-[0.35em] text-trillex-orange mb-3">— SERVICES</div>
              <h2 className="text-white text-[clamp(32px,4vw,64px)] font-black tracking-tight leading-[1.02]">
                Built for artists who <br className="hidden md:block" /> demand precision.
              </h2>
            </div>
            <p className="max-w-md text-white/55 text-[15px] leading-relaxed">
              A full-service modern label stack — from creative direction to global distribution, engineered for speed and transparency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((svc) => (
              <div
                key={svc.id}
                className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] hover:from-white/[0.08] p-7 transition-all duration-500 hover:-translate-y-1 hover:border-white/25 overflow-hidden"
              >
                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-trillex-orange/0 group-hover:bg-trillex-orange/10 blur-2xl transition-all duration-700" />
                <div className="flex items-start justify-between mb-8 relative">
                  <span className="text-[11px] tracking-[0.3em] text-white/35">{svc.tag}</span>
                  <ArrowUpRight size={18} className="text-white/40 group-hover:text-trillex-orange transition-colors duration-300" />
                </div>
                <h3 className="text-white text-xl font-bold tracking-tight mb-3">{svc.title}</h3>
                <p className="text-white/55 text-[14px] leading-relaxed">{svc.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Roster */}
        <div className="mt-28">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
            <div>
              <div className="text-[11px] tracking-[0.35em] text-trillex-orange mb-3">— ROSTER</div>
              <h2 className="text-white text-[clamp(32px,4vw,64px)] font-black tracking-tight leading-[1.02]">
                Selected artists.
              </h2>
            </div>
          </div>

          <div className="border-t border-white/10">
            {ROSTER.map((a, i) => (
              <div
                key={a.id}
                className="group flex items-center justify-between py-7 border-b border-white/10 cursor-pointer relative overflow-hidden"
              >
                <div className="flex items-center gap-6 md:gap-8 z-10 min-w-0">
                  <span className="text-[11px] tracking-[0.3em] text-white/35 w-10 shrink-0">0{i + 1}</span>
                  <h3 className="text-white text-[clamp(28px,3.4vw,56px)] font-black tracking-tight italic group-hover:translate-x-3 transition-transform duration-500">
                    {a.name}
                  </h3>
                  {a.spotifyUrl ? (
                    <a
                      href={a.spotifyUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`${a.name} on Spotify`}
                      className="shrink-0 inline-flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full text-[#1DB954] hover:text-white hover:bg-[#1DB954] transition-colors duration-300"
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12C24 5.4 18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141 4.32-1.32 9.24-.659 12.96 1.621.361.18.54.78.241 1.2zm.12-3.36c-3.84-2.28-10.26-2.52-13.92-1.38-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.021.599-1.561.3z" />
                      </svg>
                    </a>
                  ) : (
                    <span
                      aria-hidden="true"
                      className="shrink-0 inline-flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full text-[#1DB954]/30"
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12C24 5.4 18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141 4.32-1.32 9.24-.659 12.96 1.621.361.18.54.78.241 1.2zm.12-3.36c-3.84-2.28-10.26-2.52-13.92-1.38-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.021.599-1.561.3z" />
                      </svg>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-6 z-10">
                  <span className="text-white/45 text-sm tracking-widest hidden md:block">{a.genre}</span>
                  <ArrowUpRight size={22} className="text-white/40 group-hover:text-trillex-orange group-hover:rotate-45 transition-all duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-trillex-orange/0 via-trillex-orange/5 to-trillex-orange/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
